import graphql from 'babel-plugin-relay/macro';

/**
 * Fragments
 */
export const EVENT_INLINE_FRAGMENT = graphql`
  fragment eventInlineFragment on Event @inline {
    id
    name
    userId
    accountId
    description
    location
    beginsAt
    endsAt
    recurring
    timeZone
    type
    source
    createdBy
    comment
    status
    icalendar
    recurrenceID
    originalStartTime
    createdAt
    eventGuests {
      ...eventGuestInlineFragment
    }
    userProfile {
      ...userProfileBaseFragment
      ...userProfileNameFragment
      ...userProfileBaseInlineFragment
      ...userProfileNameInlineFragment
    }
    createdByUserProfile {
      ...userProfileNameInlineFragment
    }
    zoomMeetingDetails
    sessionType
    googleMeetDetails
    completedDatetime
    cancellationReasonType
    practitionerConfirmationDatetime
  }
`;

export const EVENT_FRAGMENT = graphql`
  fragment eventFragment on Event {
    id
    name
    userId
    accountId
    description
    location
    beginsAt
    endsAt
    recurring
    timeZone
    type
    source
    createdBy
    comment
    status
    icalendar
    recurrenceID
    originalStartTime
    createdAt
    session {
      ...sessionFragment
      ...sessionInlineFragment
    }
    eventGuests {
      ...eventGuestInlineFragment
      ...eventGuestFragment
    }
    userProfile {
      ...userProfileBaseInlineFragment
      ...userProfileNameInlineFragment
      ...userProfileBaseFragment
      ...userProfileNameFragment
      account {
        ...accountBaseFragment
      }
    }
    createdByUserProfile {
      ...userProfileNameInlineFragment
    }

    zoomMeetingDetails
    sessionType
    googleMeetDetails
  }
`;

export const EVENT_SALES_ORDER_FRAGMENT = graphql`
  fragment eventSalesOrderFragment on EventGuest {
    id
    event {
      id
      beginsAt
    }
  }
`;

export const EVENT_SALES_ORDER_INLINE_FRAGMENT = graphql`
  fragment eventSalesOrderInlineFragment on EventGuest @inline {
    id
    event {
      id
      beginsAt
    }
  }
`;

export const EVENT_BASE_INLINE_FRAGMENT = graphql`
  fragment eventBaseInlineFragment on Event @inline {
    id
    name
    accountId
    userId
    beginsAt
    endsAt
    timeZone
    status
    icalendar
    recurrenceID
  }
`;

export const EVENT_GUEST_ASSESSMENT_INLINE_FRAGMENT = graphql`
  fragment eventGuestAssessmentResponsesInlineFragment on EventGuestAssessmentResponses @inline {
    GAD {
      id
      questionId
      questionResponse
    }
    PHQ {
      id
      questionId
      questionResponse
    }
    Workplace {
      id
      questionId
      questionResponse
    }
    submittedAt
  }
`;

export const EVENT_GUEST_ASSESSMENT_FRAGMENT = graphql`
  fragment eventGuestAssessmentResponsesFragment on EventGuestAssessmentResponses {
    GAD {
      id
      questionId
      questionResponse
    }
    PHQ {
      id
      questionId
      questionResponse
    }
    Workplace {
      id
      questionId
      questionResponse
    }
    submittedAt
  }
`;

export const EVENT_GUEST_INLINE_FRAGMENT = graphql`
  fragment eventGuestInlineFragment on EventGuest @inline {
    id
    status
    assessmentStatus
    contact {
      ...contactBaseInlineFragment
    }
    salesOrders {
      ...salesOrderBaseInlineFragment
      salesOrderItems {
        ...salesOrderItemInlineFragment
      }
    }
    eventGuestAssessmentResponses {
      ...eventGuestAssessmentResponsesInlineFragment
    }
  }
`;

export const EVENT_GUEST_FRAGMENT = graphql`
  fragment eventGuestFragment on EventGuest {
    id
    status
    assessmentStatus
    contact {
      id
      accountId
      firstName
      lastName
      email
      source
    }
    salesOrders {
      ...salesOrderFragment
    }
    eventGuestAssessmentResponses {
      ...eventGuestAssessmentResponsesFragment
    }
  }
`;

export const EVENT_ZOOM_MEETING_FRAGMENT = graphql`
  fragment eventZoomMeetingFragment on ZoomMeetingDetails {
    id
    hostId
    joinUrl
    password
    startUrl
    status
    timezone
    uuid
  }
`;

export const EVENTS_FRAGMENT = graphql`
  fragment eventsFragment on Query @refetchable(queryName: "eventsRefetchableQuery") {
    events(first: $first, after: $after, ordering: $ordering, filter: $filter)
      @skip(if: $skip)
      @connection(key: "connection_events") {
      __id
      count
      edges {
        __id
        node {
          ...eventInlineFragment
        }
      }
    }
  }
`;

/**
 * Queries
 */
export const EVENT_GUESTS_QUERY = graphql`
  query eventGuestsQuery($first: Int!, $after: String, $filter: EventGuestFilter) {
    eventGuests(first: $first, after: $after, filter: $filter) {
      edges {
        node {
          id
          event {
            ...eventFragment
          }
        }
      }
    }
  }
`;

export const EVENTS_QUERY = graphql`
  query eventsQuery($first: Int!, $after: String, $ordering: [EventOrdering!], $filter: EventFilter, $skip: Boolean!) {
    ...eventsFragment
  }
`;

export const EVENT_QUERY = graphql`
  query eventQuery($id: ID!) {
    event(id: $id) {
      ...eventFragment
    }
  }
`;

/**
 * Mutations
 */

export const EVENT_CREATE_MUTATION = graphql`
  mutation eventCreateEventMutation($connections: [ID!]!, $input: EventCreateInput!) {
    createEvent(input: $input) {
      event @prependNode(connections: $connections, edgeTypeName: "Edge") {
        ...eventFragment
        ...eventInlineFragment
      }
    }
  }
`;

export const EVENT_CREATE_WITH_GUESTS_MUTATION = graphql`
  mutation eventCreateEventWithGuestsMutation($connections: [ID!]!, $input: CreateEventWithClientsInput!) {
    createEventWithClients(input: $input) {
      event @prependNode(connections: $connections, edgeTypeName: "Edge") {
        __id
        ...eventFragment
      }
    }
  }
`;

export const EVENT_UPDATE_MUTATION = graphql`
  mutation eventUpdateEventMutation($id: ID!, $input: EventUpdateInput!) {
    updateEvent(id: $id, input: $input) {
      event {
        ...eventFragment
      }
    }
  }
`;

export const EVENT_UPDATE_WITH_CLIENTS_MUTATION = graphql`
  mutation eventUpdateEventWithClientsMutation($id: ID!, $input: UpdateEventWithClientsInput!) {
    updateEventWithClients(id: $id, input: $input) {
      event {
        ...eventFragment
      }
    }
  }
`;

export const EVENT_RECURRING_UPDATE_MUTATION = graphql`
  mutation eventRecurringUpdateMutation($id: ID!, $input: EventRecurringUpdateInput!) {
    updateRecurringEvent(id: $id, input: $input) {
      event {
        ...eventFragment
      }
    }
  }
`;

export const EVENT_RECURRING_UPDATE_PERSISTENT_MUTATION = graphql`
  mutation eventRecurringUpdatePersistentMutation($connections: [ID!]!, $id: ID!, $input: EventRecurringUpdateInput!) {
    updateRecurringEventPersistent(id: $id, input: $input) {
      originalEvent {
        event {
          ...eventFragment
        }
      }
      newEvent {
        event @prependNode(connections: $connections, edgeTypeName: "Edge") {
          ...eventFragment
        }
      }
    }
  }
`;

export const EVENT_RECURRING_INSTANCE_DELETE_MUTATION = graphql`
  mutation eventRecurringInstanceDeleteMutation($connections: [ID!]!, $id: ID!, $recurrenceID: String!) {
    deleteRecurringEventInstance(id: $id, recurrenceID: $recurrenceID) {
      id @deleteEdge(connections: $connections)
    }
  }
`;

export const EVENT_RECURRING_PERSISTENT_DELETE_MUTATION = graphql`
  mutation eventRecurringPersistentDeleteMutation($connections: [ID!]!, $id: ID!, $recurrenceID: String!) {
    deleteRecurringEventPersistent(id: $id, recurrenceID: $recurrenceID) {
      id @deleteEdge(connections: $connections)
    }
  }
`;

export const EVENT_DELETE_MUTATION = graphql`
  mutation eventDeleteMutation($connections: [ID!]!, $id: ID!) {
    deleteEvent(id: $id) {
      id @deleteEdge(connections: $connections)
    }
  }
`;

export const EVENT_GUEST_CREATE_MUTATION = graphql`
  mutation eventGuestCreateMutation($input: EventGuestCreateInput!) {
    createEventGuest(input: $input) {
      eventGuest {
        ...eventGuestFragment
      }
    }
  }
`;

export const EVENT_GUEST_DELETE_MUTATION = graphql`
  mutation eventGuestDeleteMutation($id: ID!) {
    deleteEventGuest(id: $id) {
      id
    }
  }
`;

export const EVENT_GUESTS_DELETE_MUTATION = graphql`
  mutation eventGuestsDeleteMutation($filter: EventGuestFilter!) {
    deleteEventGuests(filter: $filter) {
      ids
    }
  }
`;

export const EVENT_GUEST_SEND_INVITATION = graphql`
  mutation eventGuestSendInvitationMutation($input: EventGuestSendInvitationInput!) {
    sendEventGuestInvitation(input: $input) {
      success
      message
    }
  }
`;

export const EVENT_PRACTITIONER_SEND_INVITATION = graphql`
  mutation eventPractitionerSendInvitationMutation($input: EventPractitionerSendInvitationInput!) {
    sendEventPractitionerInvitation(input: $input) {
      success
      message
    }
  }
`;

export const EVENT_CREATE_ZOOM_MEETING = graphql`
  mutation eventCreateZoomMeetingMutation($input: EventCreateZoomMeetingInput!) {
    createZoomMeeting(input: $input) {
      meetingDetails {
        id
        hostId
        joinUrl
        password
        startUrl
        status
        timezone
        uuid
      }
    }
  }
`;

export const EVENT_CONFIRMATION = graphql`
  mutation eventConfirmMutation($input: ConfirmEventInput!) {
    confirmEvent(input: $input)
  }
`;
